/**
 * Failed Event Monitor
 *
 * Two unrelated failure stages in one place. INGEST: events that never reached their *_events topic
 * in fh-raw-event-service — routed to a non-unifiable topic or published to the dropped-events
 * topic. SINK: events that did reach their topic but a Kafka Connect sink couldn't write
 * downstream, so Connect parked them on a DLQ topic.
 *
 * The two are shown as separate tiers rather than one total, because an ingest drop and a sink
 * write failure need different people and different fixes — a combined number would mean nothing.
 * Counts come from a durable Mongo rollup and are approximate (at-least-once), so the page says so.
 */

import { Fragment, useMemo, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  RefreshCw,
  ShieldAlert,
  Trash2,
  ChevronDown,
  ChevronRight,
  Inbox,
  PlugZap,
  Database,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Select } from '@/components/ui/select'
import { LoadingState, ErrorState, EmptyState } from '@/components/ui/loading-state'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table'
import { cn, formatDate, formatNumber } from '@/lib/utils'
import {
  getQuarantineGroups,
  getQuarantineSamples,
  getQuarantineSummary,
} from '../queries/quarantine'
import type { QuarantineGroupRow } from '../types'

const RANGE_OPTIONS = [
  { value: '1', label: 'Last 24 hours' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
]

const STAGE_OPTIONS = [
  { value: 'ALL', label: 'All stages' },
  { value: 'INGEST', label: 'Ingest only' },
  { value: 'SINK', label: 'Sink (DLQ) only' },
]

const OUTCOME_OPTIONS = [
  { value: 'ALL', label: 'All outcomes' },
  { value: 'NON_UNIFIABLE', label: 'Non-unifiable' },
  { value: 'DROPPED', label: 'Dropped' },
  { value: 'DLQ', label: 'DLQ' },
]

function isoDaysAgo(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return d.toISOString().slice(0, 10)
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function outcomeVariant(outcome: string): 'warning' | 'destructive' | 'info' | 'secondary' {
  if (outcome === 'NON_UNIFIABLE') return 'warning'
  if (outcome === 'DROPPED') return 'destructive'
  if (outcome === 'DLQ') return 'info'
  return 'secondary'
}

function groupKey(g: QuarantineGroupRow): string {
  return `${g.stage}|${g.accId}|${g.outcome}|${g.reason}|${g.eventType}|${g.connector ?? ''}|${g.dlqTopic ?? ''}`
}

export default function NonUnifiableMonitoringPage() {
  const [rangeDays, setRangeDays] = useState('7')
  const [stage, setStage] = useState('ALL')
  const [outcome, setOutcome] = useState('ALL')
  const [expanded, setExpanded] = useState<string | null>(null)

  const from = useMemo(() => isoDaysAgo(Number(rangeDays)), [rangeDays])
  const to = useMemo(() => todayIso(), [])

  const summaryQuery = useQuery({
    queryKey: ['quarantine-summary', from, to, stage, outcome],
    queryFn: () => getQuarantineSummary({ from, to, stage, outcome }),
  })

  const groupsQuery = useQuery({
    queryKey: ['quarantine-groups', from, to, stage, outcome],
    queryFn: () =>
      getQuarantineGroups({ from, to, stage, outcome, page: 0, size: 100, sort: 'count,desc' }),
  })

  const refresh = () => {
    summaryQuery.refetch()
    groupsQuery.refetch()
  }

  const summary = summaryQuery.data
  const bucketCount = (buckets: { key: string; count: number }[] | undefined, key: string) =>
    buckets?.find((b) => b.key === key)?.count ?? 0

  const ingestTotal = bucketCount(summary?.byStage, 'INGEST')
  const sinkTotal = bucketCount(summary?.byStage, 'SINK')
  const connectors = summary?.byConnector ?? []
  const topConnector = connectors[0]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Failed Events</h1>
          <p className="text-sm text-muted-foreground">
            Events that never reached their destination topic (ingest) or that a sink connector
            could not write downstream (DLQ), grouped by account, reason and type. Counts are
            approximate.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            options={RANGE_OPTIONS}
            value={rangeDays}
            onChange={(e) => setRangeDays(e.target.value)}
            className="w-40"
          />
          <Select
            options={STAGE_OPTIONS}
            value={stage}
            onChange={(e) => setStage(e.target.value)}
            className="w-40"
          />
          <Select
            options={OUTCOME_OPTIONS}
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            className="w-40"
          />
          <Button variant="outline" size="sm" onClick={refresh}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Tier 1 — summary, split by stage. No combined total: an ingest drop and a sink write
          failure are different problems, and one number covering both is not actionable. */}
      <div className="space-y-4">
        <StageTier
          label="Ingest"
          hint="Never reached the events topic — fh-raw-event-service could not identify the customer."
          total={ingestTotal}
          hasSummary={!!summary}
          cards={[
            {
              title: 'Non-unifiable',
              icon: <ShieldAlert className="h-4 w-4 text-yellow-600" />,
              value: bucketCount(summary?.byOutcome, 'NON_UNIFIABLE'),
            },
            {
              title: 'Dropped',
              icon: <Trash2 className="h-4 w-4 text-red-600" />,
              value: bucketCount(summary?.byOutcome, 'DROPPED'),
            },
          ]}
        />
        <StageTier
          label="Sink (DLQ)"
          hint="Reached the topic, but a Kafka Connect sink could not write it downstream."
          total={sinkTotal}
          hasSummary={!!summary}
          cards={[
            {
              title: 'Connectors affected',
              icon: <PlugZap className="h-4 w-4 text-blue-600" />,
              value: connectors.length,
            },
            {
              title: 'Top error',
              icon: <Database className="h-4 w-4 text-blue-600" />,
              text: topConnector ? topConnector.key : '—',
              subtext: topConnector ? `${formatNumber(topConnector.count)} events` : undefined,
            },
          ]}
        />
      </div>

      {/* Reason breakdown */}
      {summary && summary.byReason.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">By reason</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {summary.byReason.map((r) => (
                <span
                  key={r.key}
                  className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm"
                >
                  <span className="font-mono text-xs">{r.key}</span>
                  <span className="font-semibold tabular-nums">{formatNumber(r.count)}</span>
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tier 2 — grouped table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            Groups
            {groupsQuery.data ? (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                {formatNumber(groupsQuery.data.total)} total
              </span>
            ) : null}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {groupsQuery.isLoading ? (
            <LoadingState message="Loading groups…" />
          ) : groupsQuery.isError ? (
            <ErrorState message="Could not load failed event groups." onRetry={() => groupsQuery.refetch()} />
          ) : !groupsQuery.data || groupsQuery.data.groups.length === 0 ? (
            <EmptyState
              icon={<Inbox className="h-8 w-8" />}
              title="Nothing failed"
              description="No quarantined or dead-lettered events in this window."
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>Stage</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead>Outcome</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Connector</TableHead>
                    <TableHead className="text-right">Count</TableHead>
                    <TableHead>Last seen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupsQuery.data.groups.map((g) => {
                    const key = groupKey(g)
                    const isOpen = expanded === key
                    return (
                      <Fragment key={key}>
                        <TableRow
                          className="cursor-pointer"
                          onClick={() => setExpanded(isOpen ? null : key)}
                        >
                          <TableCell>
                            {isOpen ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={g.stage === 'SINK' ? 'info' : 'secondary'}>
                              {g.stage}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono">{g.accId}</TableCell>
                          <TableCell>
                            <Badge variant={outcomeVariant(g.outcome)}>{g.outcome}</Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs">{g.reason}</TableCell>
                          <TableCell>{g.eventType}</TableCell>
                          <TableCell className="font-mono text-xs text-muted-foreground">
                            {g.connector ?? '—'}
                          </TableCell>
                          <TableCell className="text-right font-semibold tabular-nums">
                            {formatNumber(g.count)}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDate(g.lastSeen)}
                          </TableCell>
                        </TableRow>
                        {isOpen && (
                          <TableRow>
                            <TableCell colSpan={9} className="bg-muted/30 p-0">
                              <SamplesPanel group={g} from={from} to={to} />
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

type TierCard = {
  title: string
  icon: ReactNode
  value?: number
  text?: string
  subtext?: string
}

// One failure stage: its own total plus the two breakdowns that matter for that stage.
function StageTier({
  label,
  hint,
  total,
  hasSummary,
  cards,
}: {
  label: string
  hint: string
  total: number
  hasSummary: boolean
  cards: TierCard[]
}) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      <Card className="sm:col-span-1">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-semibold tabular-nums">
            {hasSummary ? formatNumber(total) : '—'}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
        </CardContent>
      </Card>
      {cards.map((c) => (
        <Card key={c.title}>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              {c.icon} {c.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {c.text !== undefined ? (
              <>
                <div className="truncate font-mono text-lg font-semibold" title={c.text}>
                  {hasSummary ? c.text : '—'}
                </div>
                {c.subtext && (
                  <p className="mt-1 text-xs text-muted-foreground">{c.subtext}</p>
                )}
              </>
            ) : (
              <div className="text-3xl font-semibold tabular-nums">
                {hasSummary ? formatNumber(c.value ?? 0) : '—'}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

// Tier 3 — inline sample drill-down for a single group.
function SamplesPanel({
  group,
  from,
  to,
}: {
  group: QuarantineGroupRow
  from: string
  to: string
}) {
  const samplesQuery = useQuery({
    queryKey: ['quarantine-samples', groupKey(group), from, to],
    queryFn: () =>
      getQuarantineSamples({
        stage: group.stage,
        outcome: group.outcome,
        reason: group.reason,
        accId: group.accId,
        eventType: group.eventType,
        connector: group.connector,
        from,
        to,
        page: 0,
        size: 25,
      }),
  })

  if (samplesQuery.isLoading) {
    return <div className="p-4"><LoadingState message="Loading samples…" /></div>
  }
  if (samplesQuery.isError) {
    return (
      <div className="p-4">
        <ErrorState message="Could not load samples." onRetry={() => samplesQuery.refetch()} />
      </div>
    )
  }
  const samples = samplesQuery.data ?? []
  if (samples.length === 0) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        No samples retained for this group (samples expire after their retention window).
      </div>
    )
  }

  return (
    <div className="space-y-3 p-4">
      <div className="text-xs text-muted-foreground">
        Showing {samples.length} most recent sample{samples.length === 1 ? '' : 's'} · a bounded
        example set, not the full {formatNumber(group.count)}.
      </div>
      {samples.map((s) => (
        <div key={s.id} className="rounded-md border bg-background">
          <div className="flex flex-wrap items-center gap-3 border-b px-3 py-2 text-xs">
            <span className="font-mono text-muted-foreground">{formatDate(s.ts)}</span>
            <span className="font-mono">source: {s.sourceTopic}</span>
            {s.dlqTopic && <span className="font-mono">dlq: {s.dlqTopic}</span>}
          </div>
          {s.errorMessage && (
            <div className="border-b bg-destructive/5 px-3 py-2">
              <pre className="overflow-x-auto whitespace-pre-wrap text-xs leading-relaxed text-destructive">
                {s.errorMessage}
              </pre>
              {s.errorStacktrace && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-xs text-muted-foreground">
                    Stacktrace
                  </summary>
                  <pre className="mt-1 overflow-x-auto text-xs leading-relaxed text-muted-foreground">
                    {s.errorStacktrace}
                  </pre>
                </details>
              )}
            </div>
          )}
          <pre className={cn('overflow-x-auto p-3 text-xs leading-relaxed')}>
            {s.payloadJson ?? '(payload could not be decoded)'}
          </pre>
        </div>
      ))}
    </div>
  )
}
